import type React from "react";
import { FocusScope } from "react-aria";
import {
  Heading as RACHeading,
  type HeadingProps as RACHeadingProps,
} from "react-aria-components";

import { cn, type DisplayLevel, displayLevels } from "@/components/ui/utils";

type BaseHeadingProps = {
  displayLevel?: DisplayLevel;
  autoFocus?: boolean;
  ref?: React.RefObject<HTMLHeadingElement | HTMLDivElement>;
};

export type HeadingProps = {
  level?: DisplayLevel;
  elementType?: never;
} & RACHeadingProps;

type CustomElement = {
  level?: never;
  elementType: "div";
} & React.ComponentProps<"div">;

export function Heading({
  elementType,
  autoFocus,
  ref,
  ...props
}: BaseHeadingProps & (HeadingProps | CustomElement)) {
  if (elementType) {
    const { displayLevel = 1, className, ...restProps } = props;

    if (autoFocus) {
      return (
        <FocusScope autoFocus>
          <div
            {...restProps}
            ref={ref}
            {...(autoFocus && { tabIndex: -1 })}
            className={cn(
              [displayLevels[displayLevel], "outline-hidden"],
              className,
            )}
          />
        </FocusScope>
      );
    }
    return (
      <div
        {...restProps}
        ref={ref}
        className={cn(displayLevels[displayLevel], className)}
      />
    );
  }

  const { level = 1, displayLevel, className, ...restProps } = props;

  if (autoFocus) {
    return (
      <FocusScope autoFocus>
        <RACHeading
          {...restProps}
          ref={ref}
          level={level}
          {...(autoFocus && { tabIndex: -1 })}
          className={cn(
            [displayLevels[displayLevel ?? level], "outline-hidden"],
            className,
          )}
        />
      </FocusScope>
    );
  }

  return (
    <RACHeading
      {...restProps}
      ref={ref}
      level={level}
      className={cn(displayLevels[displayLevel ?? level], className)}
    />
  );
}

export function SubHeading({
  className,
  ref,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      ref={ref}
      className={cn("text-muted-foreground text-sm", className)}
    />
  );
}
